import { useLazyQuery } from '@apollo/client';
import {
  Currencies,
  FeatureFlagTypes,
  Frequency,
  type WorkflowCompletion,
} from '@generated/graphql';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { HoldingSelectField } from '@ovComponents/3-pattern/holdingSelectField/holdingSelect';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import { FISCAL_YEAR_SELECT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import {
  type ValidateTransferResponse,
  validateTransfer,
} from '@ovComponents/accountRestrictions';
import { TransferErrorBanner } from '@ovComponents/transferErrorBanner';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { UserContext } from '@providers/userContextProvider';
import { DEFAULT_SUPPORT_URL } from '@utils/constants';
import dayjs from 'dayjs';
import { Alert, Stack, Typography, useTheme, useToast } from 'hephaestus/web';
import { isNull, isUndefined } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import { FETCH_TOTAL_TRANSFERS } from '../queries';
import { invalidFields } from '../utils';
import {
  getDepositErrorBannerKey,
  isEftFormEnabled,
  isFhsaTransferFormEnabled,
  isFhsaTransferRuleApplicable,
} from '../utils/depositAccountRestrictions';
import {
  getDepositValidationError,
  toAmountCents,
} from '../utils/depositAmountRules';
import { buildDepositFieldOptions } from '../utils/depositFieldOptions';
import {
  depositAccountConfig,
  showFiscalYearBasedOnAccountType,
} from '../utils/depositFiscalYear';
import {
  DepositStarting,
  resolveScheduledDateMinDate,
} from '../utils/depositSchedule';
import { useDepositSchedule } from '../utils/useDepositSchedule';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import { withdrawSubAccountFilter } from '../withdrawForm/withdrawSubAccountFilter';
import { DepositExternalTransfer } from './externalTransfer/DepositExternalTransfer';
import { DepositExternalTransferTypeAlerts } from './externalTransfer/DepositExternalTransferTypeAlerts';
import { useDepositExternalTransfer } from './externalTransfer/useDepositExternalTransfer';
import { DepositInternalTransfer } from './internalTransfer/DepositInternalTransfer';
import { useDepositInternalTransfer } from './internalTransfer/useDepositInternalTransfer';

export const DepositForm = ({
  options,
  onNext,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
}: {
  options: any;
  onNext: any;
  dummyDeposit?: any;
  setDummyDeposit?: any;
  stepLoading?: boolean;
  workflowCompletion?: WorkflowCompletion;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const { userId: paramsUserId } = useParams();
  const canWriteOverrideTransferRestrictions = usePermission(
    'write:override_transfer_restrictions',
  );
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');
  const { enqueueToast } = useToast();
  const { activeEntity, activeOrganization, userContext, custodianConnection } =
    useContext(UserContext);

  const {
    applicableData,
    updateContextWithFields,
    saveTemporaryDeposit,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);

  // Seed `applicableData.type` from `options.defaultTransferType` on entry
  // when DEPOSIT_TYPE is not in the workflow. The `applicableData?.type`
  // guard makes this a no-op when DEPOSIT_TYPE precedes the form (type
  // already set) or when editing an existing deposit (type hydrated from
  // the deposit).
  useEffect(() => {
    if (mode === 'edit') return;
    if (applicableData?.type) return;
    const fallback = options?.defaultTransferType;
    if (!fallback) return;
    updateContextWithFields({ type: fallback });
  }, []);

  const [focused, setFocused] = useState<string[]>([]);
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const [totalTransfers, setTotalTransfers] = useState<number | null>(null);
  const [dateHasChanged, setDateHasChanged] = useState(false);
  const [validateErrorAmounts, setValidateErrorAmounts] = useState({
    hasError: false,
    errorMessage: '',
  });
  const [scheduleError, setScheduleError] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Prefer sub-account owner over activeEntity when the route has no :userId (household nav).
  const userIdFromSubAccount = applicableData?.subAccount?.account?.user?.id;
  const userId = paramsUserId ?? userIdFromSubAccount ?? activeEntity?.id;
  const subAccountFilter = withdrawSubAccountFilter({
    contextObjectType: applicableData?.objectType,
    contextObjectId: applicableData?.objectId,
    completionObjectType: workflowCompletion?.objectType,
    completionObjectId: workflowCompletion?.objectId,
    userId,
  });
  // Skip fetchBankAccounts until :userId or sub-account when we would otherwise use only activeEntity.
  const bankAccountsQueryUserId = paramsUserId
    ? (userId ?? '')
    : applicableData?.subAccount?.id
      ? (userId ?? '')
      : '';

  const overrideTransferRestrictions = canWriteOverrideTransferRestrictions;
  const externalTransferForm = useDepositExternalTransfer({
    deposit: applicableData,
  });
  const [fetchTotal] = useLazyQuery(FETCH_TOTAL_TRANSFERS);

  const enableMultiCurrencyDepositTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyDepositTransfers,
  );
  const scheduledDateForConfig = applicableData?.scheduledDate;
  const applicableAccountType = applicableData?.subAccount?.account
    ?.type as keyof ReturnType<typeof depositAccountConfig>;

  const depositConfig = depositAccountConfig(scheduledDateForConfig)[
    applicableAccountType
  ];
  const isCurrentContributionYearEnabled = depositConfig
    ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
    : false;

  const generateContributionYearMenuItems = () => {
    const currentScheduleDate = applicableData?.scheduledDate;
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.Now
    ) {
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs().isBefore(depositConfig.endOfPriorFiscalYear) ||
          dayjs().isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.LAST_YEAR}
            value={currentYear - 1}
          >
            {currentYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentYear}
          >
            {currentYear}
          </MenuItem>
        ),
      ];
    }
    if (
      applicableData.schedule === Frequency.OneTime &&
      starting === DepositStarting.FutureDate
    ) {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isBefore(
            depositConfig.endOfPriorFiscalYear,
          ) ||
          dayjs(currentScheduleDate).isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.LAST_YEAR}
            value={currentSelectedYear - 1}
          >
            {currentSelectedYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }
    if (applicableData.schedule !== Frequency.OneTime) {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      return [
        currentSelectedYear && (
          <MenuItem
            data-testid={FISCAL_YEAR_SELECT_TEST_ID.CURRENT_YEAR}
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }

    return [];
  };

  const hyperLinkColor = theme.color.negativeOutline;
  const supportUrl =
    translateBackendString(userContext.organization?.translatedSupportUrl) ||
    DEFAULT_SUPPORT_URL;
  const currentYear = dayjs().year();

  const clearValidateErrorAmountsState = () =>
    setValidateErrorAmounts({ hasError: false, errorMessage: '' });

  const handleTotalTransfers = async (subAccount: { id: string }) => {
    const { data } = await fetchTotal({
      variables: { input: { filter: { subAccountId: subAccount.id } } },
    });
    setTotalTransfers(data.fetchTransfers?.totalCount);
    checkValidationErrors(data.fetchTransfers?.totalCount);
  };

  const isInitiatedByRepresentative = () =>
    canWriteTransferOboClients && !canWriteTransferBasic;

  const {
    starting,
    setStarting,
    defaultScheduleOption,
    isScheduledDateDisabled,
    changeScheduledDate,
    transferDelayCheck,
    validTransferDelayCheck,
    scheduledDateError,
    scheduledDateErrorMessage,
  } = useDepositSchedule({
    options,
    deposit: applicableData,
    updateContextWithFields,
    userId,
  });

  const checkValidationErrors = (transfersCount: number | null) => {
    clearValidateErrorAmountsState();
    setScheduleError('');

    const validationError = getDepositValidationError({
      applicableData,
      options,
      transfersCount,
      organization: activeOrganization,
      t,
    });

    if (validationError?.field === 'amountCents') {
      setValidateErrorAmounts({
        hasError: true,
        errorMessage: validationError.message,
      });
    }
    if (validationError?.field === 'schedule') {
      setScheduleError(validationError.message);
    }
  };

  useEffect(() => {
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
  }, [applicableData?.amountCents, applicableData?.schedule]);

  useEffect(() => {
    const update = async () => {
      if (applicableData?.subAccount?.id) {
        await handleTotalTransfers({
          id: applicableData.subAccount.id,
          ...applicableData.subAccount,
        });
      }
    };
    update();
  }, [applicableData?.subAccount]);

  const disabled = disableForm;

  const handleValue = (value: Partial<typeof applicableData>) =>
    updateContextWithFields(value);
  const focus = (field: string) => setFocused([...focused, field]);

  const handleDateChange = async (date: any) => {
    setDateHasChanged(true);
    await changeScheduledDate(date);
  };

  useEffect(() => {
    if (applicableData?.schedule !== Frequency.OneTime) {
      handleValue({ fiscalYear: undefined });
    }
    if (isCurrentContributionYearEnabled) {
      handleValue({ fiscalYear: currentYear });
    }
  }, [applicableData?.schedule, starting, options]);

  useEffect(() => {
    if (!isFhsaTransferRuleApplicable({ applicableData })) return;
    const isFormEnabled = isFhsaTransferFormEnabled({
      applicableData,
      overrideTransferRestrictions,
    });
    setDisableForm(!isFormEnabled);
  }, [applicableData]);

  const internalTransferForm = useDepositInternalTransfer({
    deposit: applicableData,
    updateContextWithFields,
  });

  const submit = async () => {
    setIsSubmitted(true);

    // If no deposit type is selected, allow user to proceed without saving data
    if (!applicableData?.type) {
      enqueueToast(t('noDepositTypeSelectedWarning'), {
        variant: 'warning',
      });
      await onNext();
      return;
    }

    const fields = invalidFields(
      buildDepositFieldOptions({
        options,
        applicableData,
        starting,
        participantNumberEnabled: externalTransferForm.participantNumberEnabled,
        isMultiCurrencyInternalTransfer:
          internalTransferForm.isMultiCurrencyInternal,
        hasAvailableCurrencies:
          internalTransferForm.availableCurrencies.length > 0,
      }),
      applicableData,
    );

    if (isNull(totalTransfers) && applicableData?.subAccount?.id) {
      await handleTotalTransfers({
        id: applicableData.subAccount.id,
        ...applicableData.subAccount,
      });
    }
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
    if (validateErrorAmounts.hasError) {
      fields.push('amountCents');
    }

    if (externalTransferForm.hasInvalidParticipantNumber) {
      fields.push('participantNumber');
    }

    if (internalTransferForm.checkInsufficientBalance()) {
      fields.push('amountCents');
    }

    if (!validTransferDelayCheck) {
      fields.push('scheduledDate');
    }

    if (fields.length > 0) {
      setFocused([...fields]);
      return;
    }
    const transferValidation: ValidateTransferResponse = validateTransfer({
      accountTo: applicableData?.subAccount?.account?.type ?? '',
      transferType:
        applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT
          ? 'electronicFundsTransferIn'
          : applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER
            ? 'externalTransfer'
            : 'internalTransferTo',
      accountFrom: applicableData?.fromSubAccount?.account?.type,
      canOverrideTransferRestrictions: canWriteOverrideTransferRestrictions,
    });

    if (!transferValidation.isValid) {
      enqueueToast(transferValidation.message, { variant: 'error' });
      return;
    }
    if (fields.length === 0) {
      await saveTemporaryDeposit();
      await onNext();
    }
  };

  const handleEftValidation = (e: any) => {
    const isFormEnabled = isEftFormEnabled({
      selectedSubAccount: e,
      applicableData,
    });
    setDisableForm(!isFormEnabled);
  };

  const getErrorBanner = () => {
    const i18nKey = getDepositErrorBannerKey({
      applicableData,
      overrideTransferRestrictions,
    });

    if (!i18nKey) return null;

    return (
      <TransferErrorBanner
        showHeader={false}
        theme={theme}
        i18nKey={i18nKey}
        supportUrl={supportUrl}
        linkSx={{ color: hyperLinkColor }}
      />
    );
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled,
        dataTestId: 'deposit-form-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' component='div' sx={{ mb: 3 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}
      {!applicableData?.type && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {t('noDepositTypeSelected')}
        </Alert>
      )}
      {applicableData?.type === DEPOSIT_TRANSFER_TYPE.EFT && (
        <>
          {enableMultiCurrencyDepositTransfers ? (
            <>
              {options?.subAccount?.enabled &&
                applicableData.objectType !== PageObjectType.SUB_ACCOUNT && (
                  <SubAccountSelect
                    omitAny
                    sx={{ mb: 2 }}
                    filter={subAccountFilter}
                    dataTestId='deposit-form-subaccount-select'
                    label={translateBackendString(options?.subAccount?.label)}
                    onSubAccountSelect={async (e: any) => {
                      handleEftValidation(e);
                      handleValue({ subAccount: e, bankAccount: undefined });
                      await handleTotalTransfers(e);
                    }}
                    selectedSubAccount={applicableData?.subAccount}
                    onBlur={() => setFocused([...focused, 'subAccount'])}
                    error={
                      !applicableData?.subAccount?.id &&
                      focused.includes('subAccount') &&
                      options?.subAccount?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    showCurrencyAvatar={true}
                    displayCustodianAccountNumber={
                      options.displayCustodianAccountNumber
                    }
                    selectFirstSubAccountOnly={
                      options.selectFirstSubAccountOnly
                    }
                  />
                )}
              {options?.bankAccount?.enabled && (
                <BankAccountSelectField
                  key={`bank-account-select-${applicableData?.subAccount?.id}`}
                  userId={bankAccountsQueryUserId}
                  sx={{ mb: 2 }}
                  disabled={!bankAccountsQueryUserId}
                  setBankAccount={(e: any) => handleValue({ bankAccount: e })}
                  bankAccount={applicableData?.bankAccount || null}
                  accountCurrencies={
                    applicableData?.subAccount?.account?.availableCurrencies
                  }
                  onBlur={() => setFocused([...focused, 'bankAccount'])}
                  error={
                    !applicableData?.bankAccount?.id &&
                    focused.includes('bankAccount') &&
                    options?.bankAccount?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  options={options}
                  showSelectedIcon={true}
                />
              )}
            </>
          ) : (
            <>
              {options?.subAccount?.enabled &&
                applicableData?.objectType !== PageObjectType.SUB_ACCOUNT && (
                  <SubAccountSelect
                    omitAny
                    sx={{ mb: 2 }}
                    filter={subAccountFilter}
                    dataTestId='deposit-form-subaccount-select'
                    label={translateBackendString(options?.subAccount?.label)}
                    onSubAccountSelect={async (e: any) => {
                      handleEftValidation(e);
                      handleValue({ subAccount: e, bankAccount: undefined });
                      await handleTotalTransfers(e);
                    }}
                    selectedSubAccount={applicableData?.subAccount}
                    onBlur={() => setFocused([...focused, 'subAccount'])}
                    error={
                      !applicableData?.subAccount?.id &&
                      focused.includes('subAccount') &&
                      options?.subAccount?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    displayCustodianAccountNumber={
                      options.displayCustodianAccountNumber
                    }
                    selectFirstSubAccountOnly={
                      options.selectFirstSubAccountOnly
                    }
                  />
                )}
              {options?.bankAccount?.enabled && (
                <BankAccountSelectField
                  key={`bank-account-select-${applicableData?.subAccount?.id}`}
                  userId={bankAccountsQueryUserId}
                  sx={{ mb: 2 }}
                  disabled={!bankAccountsQueryUserId}
                  setBankAccount={(e: any) => handleValue({ bankAccount: e })}
                  bankAccount={applicableData?.bankAccount || null}
                  accountCurrencies={
                    applicableData?.subAccount?.account?.availableCurrencies
                  }
                  onBlur={() => setFocused([...focused, 'bankAccount'])}
                  error={
                    !applicableData?.bankAccount?.id &&
                    focused.includes('bankAccount') &&
                    options?.bankAccount?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  options={options}
                  showSelectedIcon={!!enableMultiCurrencyDepositTransfers}
                />
              )}
            </>
          )}
          {options?.holdings?.enabled && (
            <HoldingSelectField
              userId={userId ?? ''}
              sx={{ mb: 2 }}
              disabled={!applicableData?.subAccount?.account?.id}
              label={translateBackendString(options?.holdings?.label)}
              setAccountHolding={(e: any) => handleValue({ holdings: e })}
              onBlur={() => setFocused([...focused, 'holdings'])}
              selectedSubAccount={applicableData?.subAccount}
              selectedHolding={applicableData?.holdings}
              error={
                !applicableData?.holdings?.financialProduct?.cusip &&
                options?.holdings?.required !== FieldRequiredType.NOT_REQUIRED
              }
              isSubmitted={isSubmitted}
              options={options}
              holdingFilterOptions={{
                includeCash: custodianConnection?.allowCashAsDepositTarget,
              }}
            />
          )}
          {options?.fiscalYear?.enabled &&
            showFiscalYearBasedOnAccountType(
              applicableAccountType as string,
            ) && (
              <SelectField
                fullWidth
                sx={{ mb: 2 }}
                testId={FISCAL_YEAR_SELECT_TEST_ID.SELECT}
                value={applicableData?.fiscalYear}
                label={translateBackendString(options?.fiscalYear?.label)}
                onChange={(e: any) =>
                  handleValue({ fiscalYear: e.target.value })
                }
              >
                {generateContributionYearMenuItems()}
              </SelectField>
            )}
          {options?.amountCents?.enabled && (
            <AmountField
              disabled={!applicableData?.subAccount || totalTransfers === null}
              useFormatAmountValueCents
              sx={{ mb: 2 }}
              testId='deposit-form-amount-cents'
              label={translateBackendString(options?.amountCents?.label)}
              setAmount={(e: any) =>
                handleValue({
                  amountCents: toAmountCents(e),
                })
              }
              amount={applicableData?.amountCents}
              onBlur={() => setFocused([...focused, 'amountCents'])}
              error={
                (!applicableData?.amountCents &&
                  focused.includes('amountCents') &&
                  options?.amountCents?.required !==
                    FieldRequiredType.NOT_REQUIRED) ||
                (validateErrorAmounts.hasError &&
                  focused.includes('amountCents'))
              }
              errorText={
                validateErrorAmounts.hasError
                  ? validateErrorAmounts.errorMessage
                  : undefined
              }
              {...(enableMultiCurrencyDepositTransfers
                ? {
                    currency:
                      applicableData?.bankAccount?.currency ||
                      applicableData?.subAccount?.account?.baseCurrency ||
                      activeOrganization?.productShelfCurrencies?.[0] ||
                      Currencies.Cad,
                  }
                : {})}
            />
          )}
          {options?.schedule?.enabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2 }}
              testId='schedule-select'
              value={applicableData?.schedule ?? defaultScheduleOption}
              label={translateBackendString(options?.schedule?.label)}
              onChange={(e: any) => handleValue({ schedule: e.target.value })}
              onBlur={() => setFocused([...focused, 'schedule'])}
              error={
                !applicableData?.schedule &&
                focused.includes('schedule') &&
                options?.schedule?.required !== FieldRequiredType.NOT_REQUIRED
              }
              errorText={scheduleError}
            >
              <MenuItem
                data-testid='schedule-select-one-time'
                value={Frequency.OneTime}
              >
                {t('schedule.ONE_TIME')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-weekly'
                value={Frequency.Weekly}
              >
                {t('schedule.WEEKLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-bi-weekly'
                value={Frequency.BiWeekly}
              >
                {t('schedule.BI_WEEKLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-semi-monthly'
                value={Frequency.SemiMonthly}
              >
                {t('schedule.SEMI_MONTHLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-monthly'
                value={Frequency.Monthly}
              >
                {t('schedule.MONTHLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-quarterly'
                value={Frequency.Quarterly}
              >
                {t('schedule.QUARTERLY')}
              </MenuItem>
            </SelectField>
          )}
          {options?.scheduledDate?.enabled &&
            applicableData?.schedule !== Frequency.OneTime && (
              <DateField
                fullWidth
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={handleDateChange}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={resolveScheduledDateMinDate({
                  options,
                  transferDelayCheck,
                })}
                shouldDisableDate={isScheduledDateDisabled}
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage}
                autocomplete='off'
              />
            )}
          {(options?.allowOneTimeTransfersFutureDate ||
            options?.allowOneTimeTransfersNow) &&
            applicableData?.schedule === Frequency.OneTime && (
              <RadioGroup
                testId='deposit-form-starting'
                label={t('components:transferModal.starting')}
                sx={{ pb: 0 }}
                value={starting}
                onChange={(e: any) => {
                  setStarting(e.target.value);
                  if (e.target.value === DepositStarting.FutureDate) {
                    setFocused([...focused, 'scheduledDate']);
                  }
                  if (e.target.value === DepositStarting.Now) {
                    handleValue({ scheduledDate: undefined });
                  }
                }}
              >
                <Stack direction={'row'}>
                  {!isInitiatedByRepresentative() &&
                    options?.allowOneTimeTransfersNow && (
                      <Radio
                        testId='deposit-form-starting-now'
                        value={DepositStarting.Now}
                        label={t(
                          'components:transferModal.startingOptions.now',
                        )}
                      />
                    )}
                  {options?.allowOneTimeTransfersFutureDate && (
                    <Radio
                      testId='deposit-form-starting-future'
                      value={DepositStarting.FutureDate}
                      label={t(
                        'components:transferModal.startingOptions.futureDate',
                      )}
                    />
                  )}
                </Stack>
              </RadioGroup>
            )}
          {starting === DepositStarting.FutureDate &&
            applicableData?.schedule === Frequency.OneTime && (
              <DateField
                fullWidth
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={handleDateChange}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={resolveScheduledDateMinDate({
                  options,
                  transferDelayCheck,
                })}
                shouldDisableDate={isScheduledDateDisabled}
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage}
                autocomplete='off'
              />
            )}
        </>
      )}

      {options?.transferDelay?.enabled &&
        ((applicableData.schedule === Frequency.OneTime &&
          starting !== DepositStarting.Now) ||
          applicableData.schedule !== Frequency.OneTime) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            {translateBackendString(options?.transferDelayDescription)}
          </Alert>
        )}

      {applicableData?.type === DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER && (
        <DepositInternalTransfer
          deposit={applicableData}
          options={options}
          focused={focused}
          onFocus={focus}
          onChange={handleValue}
          amountError={validateErrorAmounts}
          userId={userId}
          workflowCompletion={workflowCompletion}
          transfer={internalTransferForm}
          onFormEnabledChange={(enabled) => setDisableForm(!enabled)}
          fiscalYearMenuItems={generateContributionYearMenuItems()}
        />
      )}

      {applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER && (
        <DepositExternalTransfer
          deposit={applicableData}
          options={options}
          focused={focused}
          onFocus={focus}
          onChange={handleValue}
          amountError={validateErrorAmounts}
          userId={userId}
          workflowCompletion={workflowCompletion}
          transfer={externalTransferForm}
        />
      )}

      {applicableData?.schedule !== Frequency.OneTime &&
        showFiscalYearBasedOnAccountType(
          applicableData?.subAccount?.account?.type,
        ) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            <Typography variant='bodySmall' component='p'>
              {t('fiscalYearForRecurringContributions')}
            </Typography>
          </Alert>
        )}

      {/* Later this section will be implement as editable from configuration level */}
      {applicableData?.transferType &&
        applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER && (
          <DepositExternalTransferTypeAlerts
            transferType={applicableData.transferType}
          />
        )}

      {getErrorBanner()}
    </WorkflowCompletionForm>
  );
};

export default DepositForm;
